import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ServiceCard = ({ title, description, image }: { title: string; description: string; image: string }) => (
  <motion.div 
    className="relative group"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
  >
    <div className="overflow-hidden relative">
      <img 
        src={image} 
        alt={title}
        className="w-full h-[500px] object-cover transition-transform duration-700 group-hover:scale-110"
      />
      
      {/* Soft Edge Blending */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/90 opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-transparent to-black/90 opacity-10" />
      </div>

      {/* Content Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </div>

    <div className="absolute inset-0 flex flex-col justify-end p-12 transform translate-y-20 group-hover:translate-y-0 transition-transform duration-500">
      <div className="overflow-hidden mb-4">
        <motion.div
          initial={{ y: "100%" }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl font-serif text-[#c9ab81] relative inline-block">
            {title}
            <span className="absolute left-0 bottom-0 w-full h-px bg-[#c9ab81] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
          </h3>
        </motion.div>
      </div>
      <div className="overflow-hidden">
        <motion.p 
          className="text-white/90 text-lg font-light leading-relaxed tracking-wide opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100 max-w-md"
          initial={{ y: "100%" }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          {description}
        </motion.p>
      </div>
      <motion.div 
        className="mt-6 overflow-hidden"
        initial={{ y: "100%" }}
        whileInView={{ y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <div className="h-px w-16 bg-gradient-to-r from-[#c9ab81] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200" />
      </motion.div>
    </div>
  </motion.div>
);

const AnimatedText = ({ text }: { text: string }) => {
  const words = text.split(' ');
  
  const container = {
    hidden: { opacity: 0 },
    visible: (i = 1) => ({
      opacity: 1,
      transition: { 
        staggerChildren: 0.25,
        delayChildren: 0.08 * i,
      },
    }),
  };

  const child = {
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 70,
        duration: 1.2,
      },
    },
    hidden: {
      opacity: 0,
      y: 20,
    },
  };

  return (
    <motion.p
      className="text-5xl font-serif italic text-white/90 leading-relaxed px-8"
      variants={container}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      {words.map((word, index) => (
        <motion.span
          key={index}
          variants={child}
          className="inline-block mr-[0.5em]"
        >
          {word}
        </motion.span>
      ))}
    </motion.p>
  );
};

const Services = () => {
  const { t } = useTranslation();

  const services = [
    {
      title: t('services.cards.expert.title'),
      description: t('services.cards.expert.description'),
      image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739380762/IMG_3207_2_cq4cx5.jpg"
    },
    {
      title: t('services.cards.location.title'),
      description: t('services.cards.location.description'),
      image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113219/1739112803725_nik9er.jpg"
    },
    {
      title: t('services.cards.wellness.title'),
      description: t('services.cards.wellness.description'),
      image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1739113191/1736228387442_2_ukylco.jpg"
    },
    {
      title: t('services.cards.transformation.title'),
      description: t('services.cards.transformation.description'),
      image: "https://res.cloudinary.com/dansvy9a5/image/upload/v1737622009/1736490741231_h7sanw.jpg"
    }
  ];

  return (
    <div className="section-transition py-20">
      <motion.div 
        className="text-center mb-16"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5 }}
      >
        <div className="relative max-w-5xl mx-auto">
          <motion.div 
            className="absolute -top-16 left-1/2 -translate-x-1/2 text-9xl font-serif text-[#c9ab81] opacity-0"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 0.3, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, delay: 0.8 }}
          >
            "
          </motion.div>
          <AnimatedText text={t('services.title')} />
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
        {services.map((service, index) => (
          <ServiceCard key={index} {...service} />
        ))}
      </div>
    </div>
  );
};

export default Services;